import {
  IAudit,
  IAuditNode,
  IMedicalOrderRequest,
  IPatient,
  IReport,
  IServiceOrder,
  ITemplate,
} from '@alberta/konexi-shared';
import { DocumentGenerator, ReportGeneratable } from '@alberta/swodoc-document-generator';
import { AuditData } from '@alberta/swodoc-document-generator/dist/domain/Audit/Audit';
import { Inject, Injectable } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { assignIn, cloneDeep } from 'lodash';
import { AttachmentComponentModel } from 'src/app/business/attachment/attachment-component-model';
// tslint:disable-next-line:max-line-length
import { map } from 'src/app/common/mapper/mapper';
import { MedicalOrderRequestDB, ReportDB, ServiceOrderDB } from 'src/app/common/repository/databases';
import { observe } from 'src/app/common/tracking';
import { TreeArray } from 'src/app/common/tree-array/tree-array';
import { SwodocCreateService } from 'src/app/swodoc/services/swodoc-create.service';
// Stimulsoft needs to importet from 'stimulsoft-reports-js/Scripts/stimulsoft.viewer'
// import { Stimulsoft } from 'stimulsoft-reports-js' breaks Alberta
import { Stimulsoft } from 'stimulsoft-reports-js/Scripts/stimulsoft.viewer';

import { SQLiteQueryBuilder } from '@common/storage/sqlite-query-builder';
import { AttachmentServiceToken, IAttachmentService } from '@services/attachment/attachment-service.interface';
import { MedicalOrderChain } from 'src/app/business/medical-order/medical-order-chain/medical-order-chain';
import { MedicalOrderRequestComponentModel } from 'src/app/business/medical-order/medical-order-request-component-model';
import { MedicalOrderRequestViewModel } from 'src/app/business/medical-order/view-models/medical-order-request-view-model';
import { ServiceOrderComponentModel } from 'src/app/business/service-order-entity/service-order-component-model';
import { ServiceOrderViewModel } from 'src/app/business/service-order-entity/view-model/service-order-view-model';
import { ServiceOrderMedicalOrderRequestStatusComponent } from 'src/app/patient/components/patient-detail/sections/integratedcare/service-order-detail/service-order-medicalOrder-status/service-order-medicalOrderRequest-status.component';
import { environment } from 'src/environments/environment';
import { DocumentCreateComponent } from '../../components/document/document-create/document-create.component';
import { ReportModal } from '../../components/report-modal/report-modal';
import { MedicalOrderNodeValue } from '../../models/medical-order/medical-order-node-value';
import { MedicalOrderRequestDto } from '../../models/medical-order/medical-order-request-dto.model';
import { ServiceOrderDto } from '../../models/service-order-entity/service-order-dto.model';
import { AuditAssetLoader } from '../audit/audit-asset-loader';
import { AuditReportGenerator } from '../audit/audit-report-generator';
import { QueryService } from '../query/query.service';

@Injectable({ providedIn: 'root' })
export class MedicalOrderService {
  constructor(
    private _modalController: ModalController,
    private _swodocCreateService: SwodocCreateService,
    private _attachmentModel: AttachmentComponentModel,
    private _medicalOrderChain: MedicalOrderChain,
    @Inject(AttachmentServiceToken) private _attachmentService: IAttachmentService,
    private _queryService: QueryService,
    private _medicalOrderRequestComponentModel: MedicalOrderRequestComponentModel,
    private _loadingController: LoadingController,
    private _translateService: TranslateService
  ) {}

  public async selectTemplate(patient: IPatient): Promise<ITemplate> {
    const medicalOrderTemplateModal = await this._modalController.create({
      component: DocumentCreateComponent,
      backdropDismiss: true,
      componentProps: {
        patient: cloneDeep(patient),
        entityType: 'medicalOrder',
      },
      id: 'documentCreateModal',
      cssClass: 'fullPageModal',
    });
    await medicalOrderTemplateModal.present();
    const documentResult = await medicalOrderTemplateModal.onDidDismiss();
    if (documentResult?.data) {
      return documentResult.data as ITemplate;
    }
  }

  public async addMedicalOrderRequest(
    patient: IPatient,
    selectedTemplate: ITemplate,
    selectedServiceOrders: ServiceOrderViewModel[],
    previousMedicalOrderRequest?: IMedicalOrderRequest
  ): Promise<IAudit | undefined> {
    return this._swodocCreateService.prepareMedicalOrderAudit(
      selectedTemplate,
      {
        patient: patient,
        selectedServiceOrders,
      },
      previousMedicalOrderRequest
    );
  }

  public async getPreviousMedicalOrderRequestWithinSameIntegratedCareAndTemplate(
    templateId: string,
    patientId: string,
    serviceOrderIds: string[]
  ): Promise<IMedicalOrderRequest | undefined> {
    // Step 1: Fetch medical order requests for the same patient and template
    const previousMedicalOrderRequestsForSamePatient = await this.fetchMedicalOrderRequestsByPatientAndTemplate(
      patientId,
      templateId
    );

    // Step 2: Filter requests based on integrated care IDs
    const filteredRequests = await this.filterRequestsByIntegratedCareIds(
      previousMedicalOrderRequestsForSamePatient,
      serviceOrderIds
    );

    // Step 3: Sort and get the most recent request
    return this.getMostRecentRequest(filteredRequests);
  }

  /**
   * Fetches medical order requests for a specific patient and template
   */
  private async fetchMedicalOrderRequestsByPatientAndTemplate(
    patientId: string,
    templateId: string
  ): Promise<IMedicalOrderRequest[]> {
    return await this._queryService.raw<IMedicalOrderRequest>(
      {
        sql: new SQLiteQueryBuilder()
          .select('value')
          .fromFts(MedicalOrderRequestDB)
          .innerJoin(MedicalOrderRequestDB, 'id', '=', 'key')
          .where('patientId', '=', patientId)
          .and('templateId', '=', templateId)
          .asString(),
        aggregation: [
          {
            $match: {
              archived: { $ne: true },
              patientId: { $eq: patientId },
              templateId: { $eq: templateId },
            },
          },
        ],
      },
      MedicalOrderRequestDB
    );
  }

  /**
   * Filters medical order requests based on whether their service orders have matching integrated care IDs
   * Note: If this gets too inperformant in the future we need to extend the medicalOrderRequest DataModel
   * and write the integratedCare ID there as well
   */
  private async filterRequestsByIntegratedCareIds(
    requests: IMedicalOrderRequest[],
    serviceOrderIds: string[]
  ): Promise<IMedicalOrderRequest[]> {
    const filteredRequests: IMedicalOrderRequest[] = [];

    for (const request of requests) {
      const serviceOrder = await this._queryService.get<IServiceOrder>(request.serviceOrderId, ServiceOrderDB);
      if (serviceOrderIds.includes(serviceOrder?.integratedCareId)) {
        filteredRequests.push(request);
      }
    }

    return filteredRequests;
  }

  /**
   * Returns the most recent medical order request from the provided list
   */
  private getMostRecentRequest(requests: IMedicalOrderRequest[]): IMedicalOrderRequest | undefined {
    if (!requests || requests.length === 0) {
      return undefined;
    }

    return requests.sort(
      (a, b) => new Date((b as any).createdAt).getTime() - new Date((a as any).createdAt).getTime()
    )[0];
  }

  /**
   * Print the medicalOrderRequest request in the browser
   */
  public async openBrowserPrintDialogue(audit: IAudit, MedicalOrderRequestNode: any): Promise<void> {
    Stimulsoft.Base.StiLicense.key = environment.stimulsoftLicense;
    const loading = await this._loadingController.create({
      message: this._translateService.instant('medicalOrder.create.preparingPrint'),
    });
    await loading.present();
    const closeModal = async () => {
      await loading.dismiss().catch(error => console.log(error));
    };
    const additionalProperties = await this.getAdditionalPropertiesFromAudit(audit);
    const medicalOrderRequest = await this.generateMedicalOrderRequest(MedicalOrderRequestNode, additionalProperties);
    const reportEntity = await this._queryService.get<IReport>(audit.templateId, ReportDB);
    const report = new Stimulsoft.Report.StiReport();
    report.load(reportEntity.report);
    const dataSet = new Stimulsoft.System.Data.DataSet('swodoc');
    /*
      This is a fix for Stimulsoft to display the correct quantity in the report.
      Stimulsoft has a problem to read the quantity from the position.dosage.quantity. It always displays 0.
      We dont need the position.quantity (Anzahl der Aufgaben) in the report,
      so we can overwrite it with the position.dosage.quantity.
      With this workaround we can display the correct quantity in the report.
    */
    medicalOrderRequest.medicalOrders[0].positions.forEach(position => {
      position.quantity = position.dosage.quantity;
    });
    dataSet.readJson(medicalOrderRequest);
    report.regData(dataSet.dataSetName, '', dataSet);
    report.dictionary.synchronize();
    // This is fire and forget. We dont know when the popup will be opened
    report.print(Stimulsoft.Report.StiPagesRange.All, Stimulsoft.Report.Export.StiHtmlExportMode.Div);
    await closeModal();
  }

  public async getAdditionalPropertiesFromAudit(medicalOrderAudit: IAudit): Promise<any> {
    const documentGenerator = new DocumentGenerator(
      new AuditAssetLoader(this._attachmentModel, this._attachmentService),
      new AuditReportGenerator() as unknown as ReportGeneratable
    );
    const auditWithoutMedicalOrderElements = this.filterOutMedicalOrderElements(medicalOrderAudit);
    const auditKeyValuePairs = await documentGenerator.generate<Record<string, string>>(
      auditWithoutMedicalOrderElements
    );
    return auditKeyValuePairs?.additionalProperties;
  }

  public async generateMedicalOrderRequests(
    medicalOrderAuditNodes: IAuditNode[],
    additionalProperties: any
  ): Promise<MedicalOrderRequestViewModel[]> {
    const medicalOrderNodes = assignIn(new TreeArray<IAuditNode>(), medicalOrderAuditNodes).filterRec(
      node => node.nodeType_Id === 'medicalOrderData'
    );

    const medicalOrders: MedicalOrderRequestViewModel[] = [];
    for (const medicalOrderNode of medicalOrderNodes) {
      const medicalOrderDto = await this.generateMedicalOrderRequest(medicalOrderNode, additionalProperties);
      medicalOrders.push(medicalOrderDto);
    }
    for (const medicalOrder of medicalOrders) {
      await this._medicalOrderRequestComponentModel.create(map(medicalOrder, new MedicalOrderRequestDto()));
    }

    return medicalOrders;
  }

  public async generateMedicalOrderRequest(
    medicalOrderAuditNode: IAuditNode,
    additionalProperties: any
  ): Promise<MedicalOrderRequestViewModel> {
    const medicalRequestViewModel: MedicalOrderRequestViewModel = observe(new MedicalOrderRequestViewModel());
    await this._medicalOrderChain.handleRequest(
      medicalRequestViewModel,
      medicalOrderAuditNode.responses[0].value as unknown as MedicalOrderNodeValue
    );
    medicalRequestViewModel.additionalProperties = additionalProperties as AuditData;
    return medicalRequestViewModel;
  }

  public async preview(audit: IAudit, node: any): Promise<void> {
    const loading = await this._loadingController.create({
      message: this._translateService.instant('components.swodoc.loadingPreview'),
    });
    await loading.present();
    const closeModal = async () => {
      await loading.dismiss().catch(error => console.log(error));
    };
    const additionalProperties = await this.getAdditionalPropertiesFromAudit(audit);
    const medicalOrderRequest = await this.generateMedicalOrderRequest(node, additionalProperties);
    const reportEntity = await this._queryService.get<IReport>(audit.templateId, ReportDB);

    const report = new Stimulsoft.Report.StiReport();
    report.load(reportEntity.report);
    const dataSet = new Stimulsoft.System.Data.DataSet('swodoc');
    /*
      This is a fix for Stimulsoft to display the correct quantity in the report.
      Stimulsoft has a problem to read the quantity from the position.dosage.quantity. It always displays 0.
      We dont need the position.quantity (Anzahl der Aufgaben) in the report,
      so we can overwrite it with the position.dosage.quantity.
      With this workaround we can display the correct quantity in the report.
    */
    medicalOrderRequest.medicalOrders[0].positions.forEach(position => {
      position.quantity = position.dosage.quantity;
    });
    dataSet.readJson(medicalOrderRequest);
    report.regData(dataSet.dataSetName, '', dataSet);
    report.dictionary.synchronize();
    await closeModal();
    const modal = await this._modalController.create({
      component: ReportModal,
      componentProps: { report, name: 'Verordnungsanforderung', showSaveButton: true },
      cssClass: 'fullPageModal',
    });
    await modal.present();
  }

  public async changeMedicalOrderRequestStatus(
    OrderViewModel: ServiceOrderViewModel,
    componentModel: ServiceOrderComponentModel
  ): Promise<void> {
    const statusModal = await this._modalController.create({
      component: ServiceOrderMedicalOrderRequestStatusComponent,
      componentProps: {
        medicalOrderRequestStatus: OrderViewModel.medicalOrderRequestStatus,
      },
      cssClass: 'fullPageModal',
    });
    await statusModal.present();
    const response = await statusModal.onDidDismiss();
    if (response.data) {
      const serviceOrderViewModel = observe(map(OrderViewModel, new ServiceOrderViewModel()));
      serviceOrderViewModel.medicalOrderRequestStatus = response.data;
      await componentModel.edit(serviceOrderViewModel, map(serviceOrderViewModel, new ServiceOrderDto()), {
        ignoreUpdatedAt: true,
        noNotification: true,
      });
    }
  }

  private filterOutMedicalOrderElements(medicalOrderAudit: IAudit): IAudit {
    const audit = cloneDeep(medicalOrderAudit);
    audit.auditNodes.forEach(section => {
      section.children.map(child => {
        if ((child.nodeType_Id as any) === 'medicalOrderData') {
          if (section.children.length > 1) {
            section.children = section.children.filter(item => (item.nodeType_Id as any) !== 'medicalOrderData');
            return;
          } else {
            audit.auditNodes = audit.auditNodes.filter(auditNode => auditNode !== section);
            return;
          }
        }
      });
    });
    return audit;
  }
}
